/**
 * AuthGate — Login con Microsoft y GitHub via Azure Static Web Apps auth.
 *
 * Cómo funciona:
 * - En producción (Azure): llama a /.auth/me para saber si hay sesión activa.
 *   Los botones redirigen a /.auth/login/aad (Microsoft) o /.auth/login/github.
 *   El logout redirige a /.auth/logout.
 * - En desarrollo (localhost): /.auth/me devuelve null, se muestra un bypass
 *   para poder trabajar sin necesidad de autenticación real.
 */
import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

const IS_DEV = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

const supabase = createClient(
  "https://ubqwzzxbhvdsairrxhsw.supabase.co",
  "sb_publishable_LsufqZX5suRCd1_F5B-48Q_WaKgRki5"
);

// Llama al endpoint de Azure SWA para obtener el usuario autenticado
async function fetchAzureUser() {
  try {
    const res = await fetch("/.auth/me");
    if (!res.ok) return null;
    const data = await res.json();
    return data?.clientPrincipal ?? null;
  } catch {
    return null;
  }
}

// Registra o actualiza el usuario en Supabase (upsert por userId)
async function registerUser(user) {
  try {
    await supabase.from("users").upsert({
      id: `${user.identityProvider}_${user.userId}`,
      provider: user.identityProvider,
      user_details: user.userDetails,   // email en Microsoft, username en GitHub
      last_login: new Date().toISOString(),
    }, { onConflict: "id" });
  } catch (err) {
    console.warn("No se pudo registrar el usuario:", err.message);
  }
}

export default function AuthGate({ children }) {
  const [user, setUser] = useState(undefined); // undefined = cargando
  const [error] = useState(null);

  useEffect(() => {
    fetchAzureUser().then(u => {
      setUser(u);
      if (u && !u._devMode) registerUser(u);
    });
  }, []);

  const handleLogout = () => {
    window.location.href = "/.auth/logout?post_logout_redirect_uri=/";
  };

  const handleDevBypass = () => {
    setUser({
      identityProvider: "dev",
      userId: "dev-user",
      userDetails: "desarrollador@local",
      userRoles: ["authenticated"],
      _devMode: true,
    });
  };

  // --- Pantalla de carga inicial ---
  if (user === undefined) {
    return (
      <div style={styles.screen}>
        <div style={styles.spinner} />
      </div>
    );
  }

  // --- Pantalla de login ---
  if (!user) {
    return (
      <div style={styles.screen}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Logo CYT encima de la card */}
        <div style={{ marginBottom: 20, opacity: 0.9 }}>
          <svg viewBox="0 0 220 52" width="180" height="42" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="lg1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a855f7"/>
                <stop offset="100%" stopColor="#7c3aed"/>
              </linearGradient>
              <linearGradient id="lg2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#a855f7"/>
                <stop offset="100%" stopColor="#818cf8"/>
              </linearGradient>
            </defs>
            <circle cx="10" cy="26" r="4.5" fill="url(#lg1)"/>
            <circle cx="28" cy="10" r="5.5" fill="url(#lg1)"/>
            <circle cx="28" cy="42" r="3.5" fill="#7c3aed" opacity="0.7"/>
            <line x1="13.5" y1="23.5" x2="24" y2="13" stroke="url(#lg1)" strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="13.5" y1="28" x2="25" y2="39.5" stroke="#7c3aed" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
            <line x1="28" y1="15.5" x2="28" y2="38.5" stroke="#a855f7" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
            <text x="40" y="36" fontFamily="'Segoe UI', system-ui, sans-serif" fontWeight="800" fontSize="30" fill="url(#lg2)" letterSpacing="-0.5">CYT</text>
            <text x="41" y="48" fontFamily="'Segoe UI', system-ui, sans-serif" fontWeight="500" fontSize="9.5" fill="#64748b" letterSpacing="2.5">COMUNICACIONES</text>
          </svg>
        </div>

        <div style={styles.card}>
          <div style={styles.logoWrap}>
            <span style={styles.logoIcon}>🤖</span>
          </div>

          <h1 style={styles.title}>Bot Designer</h1>
          <p style={styles.subtitle}>Diseñador visual de bots para WhatsApp</p>

          {error && <p style={styles.errorMsg}>{error}</p>}

          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
            {/* Microsoft */}
            <a
              href="/.auth/login/aad?post_login_redirect_uri=/"
              style={styles.providerBtn}
            >
              <MicrosoftIcon />
              Continuar con Microsoft
            </a>

            {/* GitHub */}
            <a
              href="/.auth/login/github?post_login_redirect_uri=/"
              style={{ ...styles.providerBtn, background: "#161b22", borderColor: "#30363d" }}
            >
              <GitHubIcon />
              Continuar con GitHub
            </a>
          </div>

          {/* Bypass solo en desarrollo local */}
          {IS_DEV && (
            <div style={{ marginTop: 20, width: "100%", borderTop: "1px solid #1e293b", paddingTop: 16 }}>
              <p style={{ ...styles.legal, color: "#f59e0b", marginBottom: 8 }}>
                ⚠️ Modo desarrollo — auth de Azure no disponible en localhost
              </p>
              <button onClick={handleDevBypass} style={styles.devBtn}>
                Entrar sin autenticación (solo dev)
              </button>
            </div>
          )}

          <p style={styles.legal}>
            Solo usamos tu identidad para identificarte. No almacenamos datos adicionales.
          </p>
        </div>
        </div>
      </div>
    );
  }

  // --- Autenticado ---
  // Normalizar datos según proveedor
  const displayName = user.userDetails || user.userId || "Usuario";
  const provider = user.identityProvider;
  const avatarLetter = displayName.charAt(0).toUpperCase();

  return children({ user, displayName, avatarLetter, provider, onLogout: handleLogout });
}

// ============================================================
// ICONOS SVG
// ============================================================
function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" style={{ flexShrink: 0 }}>
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#e2e8f0" style={{ flexShrink: 0 }}>
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12"/>
    </svg>
  );
}

// ============================================================
// ESTILOS
// ============================================================
const styles = {
  screen: {
    width: "100%", height: "100vh",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg, #0a0e1a 0%, #0f172a 50%, #1a1040 100%)",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  card: {
    background: "#111827",
    border: "1px solid #1e293b",
    borderRadius: 16,
    padding: "40px 36px",
    width: "100%", maxWidth: 380,
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 8,
    boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
  },
  logoWrap: {
    width: 72, height: 72, borderRadius: "50%",
    background: "linear-gradient(135deg, #1e40af, #7c3aed)",
    display: "flex", alignItems: "center", justifyContent: "center",
    marginBottom: 8,
  },
  logoIcon: { fontSize: 36 },
  title: {
    margin: 0, fontSize: 26, fontWeight: 700, color: "#f1f5f9",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: 0, fontSize: 13, color: "#64748b", textAlign: "center",
  },
  providerBtn: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "11px 20px", borderRadius: 8, border: "1px solid #334155",
    background: "#1e293b", color: "#e2e8f0",
    fontSize: 14, fontWeight: 500, cursor: "pointer",
    width: "100%", justifyContent: "center",
    textDecoration: "none",
    transition: "background 0.15s",
    boxSizing: "border-box",
  },
  devBtn: {
    width: "100%", padding: "8px 0", borderRadius: 6,
    border: "1px dashed #78350f", background: "transparent",
    color: "#f59e0b", fontSize: 12, cursor: "pointer",
  },
  errorMsg: {
    margin: 0, fontSize: 12, color: "#f87171", textAlign: "center",
  },
  legal: {
    margin: 0, fontSize: 11, color: "#475569",
    textAlign: "center", lineHeight: 1.5, marginTop: 8,
  },
  spinner: {
    width: 40, height: 40, borderRadius: "50%",
    border: "3px solid #1e293b",
    borderTopColor: "#3b82f6",
    animation: "spin 0.8s linear infinite",
  },
};

if (!document.getElementById("authgate-styles")) {
  const s = document.createElement("style");
  s.id = "authgate-styles";
  s.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}
